import React, { useContext, useState, useEffect, useCallback } from "react";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, updateEmail, updatePassword, updateProfile } from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from '../config/firebase';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data from Firestore
  const fetchUserData = useCallback(async (user) => {
    if (!user) {
      setUserData(null);
      return null;
    }

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        return data;
      } else {
        // Create default user document if it doesn't exist
        const defaultData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          createdAt: serverTimestamp(),
          subscriptionTier: null,
          subscriptionStatus: null,
          auditsUsed: 0,
          auditsLimit: 0
        };
        await setDoc(doc(db, "users", user.uid), defaultData);
        setUserData(defaultData);
        return defaultData;
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUserData(null);
      return null;
    }
  }, []);

  // Refresh user data
  const refreshUserData = useCallback(async () => {
    if (currentUser) {
      return await fetchUserData(currentUser);
    }
    return null;
  }, [currentUser, fetchUserData]);

  // Sign up with email and password
  async function signup(email, password, displayName) {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Update profile with display name
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }

    // Create user document in Firestore
    await setDoc(doc(db, "users", result.user.uid), {
      uid: result.user.uid,
      email: result.user.email,
      displayName: displayName || '',
      createdAt: serverTimestamp(),
      subscriptionTier: null,
      subscriptionStatus: null,
      auditsUsed: 0,
      auditsLimit: 0
    });

    return result;
  }

  // Sign in with email and password (aliased as both login and signin)
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Alias for login - some components use signin
  const signin = login;

  function logout() {
    setUserData(null);
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateUserEmail(email) {
    return updateEmail(currentUser, email);
  }

  function updateUserPassword(password) {
    return updatePassword(currentUser, password);
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserData(user);
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserData]);

  // Check if user is admin
  const isAdmin = userData?.isAdmin === true;

  // Check if user has active subscription (admins always have access)
  const hasActiveSubscription = isAdmin || userData?.subscriptionStatus === 'active';

  const value = {
    currentUser,
    userData,
    loading,
    isAdmin,
    hasActiveSubscription,
    login,
    signin,  // Alias for login
    signup,
    logout,
    resetPassword,
    updateUserEmail,
    updateUserPassword,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
